import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { UserIcon, IdentificationIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

import { InputTextModern } from '../../components/inputs/InputTextModern';
import { InputPhone } from '../../components/inputPhone/InputPhone';
import { Button } from '../../components/buttons/Button';
import { ComboBox } from '../../components/comboBox/ComboBox';
import { InputDateModern } from '../../components/inputDateModern/InputDateModern';
import { StepIndicator } from '../../components/stepIndicator/StepIndicator';
import { BrandingLogo } from '../../components/brandingLogo/BrandingLogo';
import { CopyrightFooter } from '../../components/copyrightFooter/CopyrightFooter';
import { ImageUploadDropzone } from '../../components/imageUpload/ImageUploadDropzone';

import { resetEmailVerification } from '../../redux/reducers/preRegistration';
import { registerPreRegitration, validatePaymentVoucher, emailVerification } from '../../redux/actions/preRegistration';
import { optionsCoordinadors } from '../../redux/actions/options';

import { levelEducation, locationState } from '../../static/data';
import { isValidPhoneNumber } from '../../common/phoneInputValid';
import { PreRegistrationTermsBody } from './PreRegistrationTermsBody';

const WIZARD_STEPS = [
	{ id: 1, label: 'Datos', description: 'Personales' },
	{ id: 2, label: 'Términos', description: 'Aceptación' },
	{ id: 3, label: 'Pago', description: 'Comprobante' },
	{ id: 4, label: 'Validación', description: 'En curso' },
];

function resolveWizardStep(userPreRegister) {
	if (!userPreRegister) return 1;
	const st = userPreRegister.status;
	if (st === 'prospecto') return 3;
	if (st === 'validando' || st === 'completado') return 4;
	return 1;
}

/** Primer mensaje de error del paso 1, o `null` si todo es válido (misma lógica que los toasts de `validarPaso1`). */
function getPaso1ValidationMessage(formData, selectDate) {
	if (formData.firstName.trim() === '') return 'Ingresa tu nombre';
	if (formData.lastName.trim() === '') return 'Ingresa tu apellido paterno';
	if (formData.secondSurname.trim() === '') return 'Ingresa tu apellido materno';
	if (formData.phone.trim() === '') return 'Ingresa tu teléfono';
	if (!isValidPhoneNumber(formData.phone)) return 'El número de teléfono debe contener exactamente 10 dígitos.';
	if (selectDate === null) return 'Ingresa tu fecha de nacimiento';
	if (formData.location === undefined || !formData.location?.description) return 'Ingresa tu ubicación';
	if (formData.education === undefined || !formData.education?.description) return 'Ingresa tu nivel educativo';
	return null;
}

export const PreRegistrationUnified = () => {
	const dispatch = useDispatch();
	const { email, userPreRegister } = useSelector((s) => s.preRegistration);
	const { coordinadors } = useSelector((s) => s.options);

	const [wizardStep, setWizardStep] = useState(() => resolveWizardStep(userPreRegister));
	const [findLocation, setFindLocation] = useState('');
	const [findLevelEducation, setFindLevelEducation] = useState('');
	const [findCoordinador, setFindCoordinador] = useState('');
	const [selectDate, setSelectDate] = useState(null);
	const [loadingForm, setLoadingForm] = useState(false);
	const [loadingPayment, setLoadingPayment] = useState(false);

	const [formData, setFormData] = useState({
		firstName: '',
		secondName: '',
		lastName: '',
		secondSurname: '',
		email: email || '',
		phone: '',
		dateBirth: '',
		location: '',
		education: '',
		usageRights: false,
		rfc: '',
	});

	const [paymentForm, setPaymentForm] = useState({
		coordinador: '',
		file: null,
		email: email || '',
		id: userPreRegister?._id,
	});

	useEffect(() => {
		setWizardStep(resolveWizardStep(userPreRegister));
	}, [userPreRegister?._id, userPreRegister?.status]);

	useEffect(() => {
		setFormData((p) => ({ ...p, email: email || '' }));
		setPaymentForm((p) => ({ ...p, email: email || '', id: userPreRegister?._id }));
	}, [email, userPreRegister?._id]);

	useEffect(() => {
		dispatch(optionsCoordinadors());
	}, [dispatch]);

	useEffect(() => {
		setFormData((p) => ({ ...p, dateBirth: selectDate }));
	}, [selectDate]);

	const isPaso1Complete = useMemo(
		() => getPaso1ValidationMessage(formData, selectDate) === null,
		[
			formData.firstName,
			formData.lastName,
			formData.secondSurname,
			formData.phone,
			formData.location,
			formData.education,
			selectDate,
		]
	);

	const canSubmitStep2 = useMemo(
		() => isPaso1Complete && formData.usageRights,
		[isPaso1Complete, formData.usageRights]
	);

	const canSubmitStep3 = useMemo(() => {
		if (!paymentForm.file) return false;
		const coord = paymentForm.coordinador?.value ?? paymentForm.coordinador ?? '';
		if (String(coord).trim() === '') return false;
		return true;
	}, [paymentForm.file, paymentForm.coordinador]);

	const goBackStart = () => {
		dispatch(resetEmailVerification());
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const validarPaso1 = () => {
		const msg = getPaso1ValidationMessage(formData, selectDate);
		if (msg) {
			toast.error(msg);
			return false;
		}
		return true;
	};

	const handleNextPaso1 = () => {
		if (!validarPaso1()) return;
		setWizardStep(2);
	};

	const handleRegister = () => {
		if (!formData.usageRights) {
			toast.error('Debes aceptar los términos y condiciones para continuar con el proceso de registro');
			return;
		}
		if (!validarPaso1()) return;
		const updateState = { ...formData };
		updateState.education = formData.education.description;
		updateState.location = formData.location.description;
		updateState.dateBirth = selectDate;
		setLoadingForm(true);
		const payload = { ...updateState, phone: updateState.phone.replace(/\D/g, '') };
		dispatch(registerPreRegitration(payload))
			.then((result) => {
				if (result?.status === 201) {
					toast.success(result.message);
					setWizardStep(3);
				} else {
					toast.error(result?.message || 'No se pudo completar el registro');
				}
				setLoadingForm(false);
			});
	};

	const handleFileChange = (file) => {
		setPaymentForm((p) => ({ ...p, file }));
	};

	const handleFileRemove = () => {
		setPaymentForm((p) => ({ ...p, file: null }));
	};

	const validPayment = (data) => {
		if (data.file === null) {
			toast.error('Seleccione su comprobante de pago.');
			return false;
		}
		const coord = data.coordinador?.value ?? data.coordinador ?? '';
		if (String(coord).trim() === '') {
			toast.error('Seleccione a su coordinador.');
			return false;
		}
		return true;
	};

	const handleSubmitPayment = () => {
		if (!userPreRegister?._id) {
			toast.error('No se encontró tu registro. Verifica de nuevo tu correo.');
			return;
		}
		const updateState = {
			...paymentForm,
			coordinador: paymentForm.coordinador?.value || paymentForm.coordinador || '',
			email,
			id: userPreRegister._id,
		};
		if (!validPayment(updateState)) return;
		setLoadingPayment(true);
		dispatch(
			validatePaymentVoucher({
				file: updateState.file,
				coordinador: updateState.coordinador,
				email: updateState.email,
				id: updateState.id,
				amount: 0,
				account: undefined,
			})
		)
			.then((result) => {
				if (result?.status === 200) {
					toast.success(result.message);
					dispatch(emailVerification(email));
					setWizardStep(4);
				} else if (result?.message) {
					toast.error(result.message);
				}
			})
			.finally(() => setLoadingPayment(false));
	};

	const filteredLocations =
		findLocation === ''
			? locationState
			: locationState.filter((loc) =>
					loc.description
						.toLowerCase()
						.replace(/\s+/g, '')
						.includes(findLocation.toLowerCase().replace(/\s+/g, ''))
			  );

	const filteredLevelEducations =
		findLevelEducation === ''
			? levelEducation
			: levelEducation.filter((lev) =>
					lev.description
						.toLowerCase()
						.replace(/\s+/g, '')
						.includes(findLevelEducation.toLowerCase().replace(/\s+/g, ''))
			  );

	const filteredCoordinadors =
		findCoordinador === ''
			? coordinadors
			: coordinadors.filter((c) =>
					c.description
						.toLowerCase()
						.replace(/\s+/g, '')
						.includes(findCoordinador.toLowerCase().replace(/\s+/g, ''))
			  );

	const wizardHeader = useMemo(() => {
		switch (wizardStep) {
			case 1:
				return {
					title: 'Bienvenido a CFA',
					subtitle: 'Completa tus datos personales para continuar.',
				};
			case 2:
				return {
					title: 'Términos y condiciones',
					subtitle: 'Lee y acepta para registrar tu solicitud y recibir los datos de pago por correo.',
				};
			case 3:
				return {
					title: 'Comprobante de pago',
					subtitle:
						'Sube tu comprobante y elige a tu coordinador. Revisa el correo con las instrucciones de pago si aún no has pagado.',
				};
			case 4:
				return {
					title:
						userPreRegister?.status === 'completado'
							? '¡Registro completado!'
							: 'Estamos validando tu pago',
					subtitle:
						userPreRegister?.status === 'completado'
							? 'Tu acceso ya está disponible. Puedes iniciar sesión con el correo que registraste.'
							: `Cuando la validación termine enviaremos un correo a ${email} con tus accesos.`,
				};
			default:
				return { title: '', subtitle: '' };
		}
	}, [wizardStep, userPreRegister?.status, email]);

	const renderStep = () => {
		switch (wizardStep) {
			case 1:
				return (
					<div className='flex w-full flex-col gap-6 py-4 md:py-6'>
						<div className='relative z-10'>
						<div className='flex items-center'>
							<div className='w-full grid grid-cols-1 md:grid-cols-2 gap-6 animate-slideUp'>
								<InputTextModern id='firstName' name='firstName' type='text' label='Nombre' onChange={onChange} value={formData.firstName} placeholder='Ingresa tu nombre' disabled={false} icon={UserIcon} required />
								<InputTextModern id='secondName' name='secondName' type='text' label='Otros nombres' onChange={onChange} value={formData.secondName} placeholder='Ingresa tus otros nombres' disabled={false} icon={UserIcon} />
								<InputTextModern id='lastName' name='lastName' type='text' label='Apellido Paterno' onChange={onChange} value={formData.lastName} placeholder='Ingresa tu apellido paterno' disabled={false} icon={UserIcon} required />
								<InputTextModern id='secondSurname' name='secondSurname' type='text' label='Apellido Materno' onChange={onChange} value={formData.secondSurname} placeholder='Ingresa tu apellido materno' disabled={false} icon={UserIcon} required />
								<InputPhone id='phone' name='phone' label='Teléfono' onChange={onChange} value={formData.phone} placeholder='(555) 123 4567' disabled={false} required />
								<InputDateModern
									id='dateBirth'
									label='Fecha de nacimiento'
									onChange={setSelectDate}
									value={selectDate}
									placeholder='Selecciona tu fecha de nacimiento'
									maxDate={new Date()}
									required
									popperPlacement='top-start'
								/>
								<ComboBox
									inputId='pre-reg-location'
									label='Lugar de residencia'
									required
									filterData={filteredLocations}
									query={findLocation}
									setQuery={setFindLocation}
									selected={formData}
									setSelected={setFormData}
									placeholder='Selecciona el estado donde vives'
									property='location'
								/>
								<ComboBox
									inputId='pre-reg-education'
									label='Nivel educativo'
									required
									filterData={filteredLevelEducations}
									query={findLevelEducation}
									setQuery={setFindLevelEducation}
									selected={formData}
									setSelected={setFormData}
									placeholder='Selecciona tu nivel educativo'
									property='education'
								/>
								<InputTextModern id='rfc' name='rfc' type='text' label='RFC' onChange={onChange} value={formData.rfc} placeholder='Ingresa tu RFC (opcional)' disabled={false} icon={IdentificationIcon} />
							</div>
						</div>
						</div>
						<div className='relative z-0 flex w-full justify-between gap-4 pt-2'>
							<div className='min-w-0 flex-1'>
								<Button type='button' variant='outlineCta' onClick={goBackStart}>
									Cancelar
								</Button>
							</div>
							<div className='min-w-0 flex-1'>
								<Button
									type='button'
									variant='gradient'
									fullWidth
									disabled={!isPaso1Complete}
									onClick={handleNextPaso1}
								>
									Siguiente
								</Button>
							</div>
						</div>
					</div>
				);
			case 2:
				return (
					<div className='flex w-full flex-col gap-6 py-4 md:py-6'>
						<div className='relative z-10'>
						<div className='flex items-center'>
							<PreRegistrationTermsBody formData={formData} setFormData={setFormData} />
						</div>
						</div>
						<div className='relative z-0 flex w-full justify-between gap-4 pt-2'>
							<div className='min-w-0 flex-1'>
								<Button
									type='button'
									variant='outlineCta'
									disabled={loadingForm}
									onClick={() => setWizardStep(1)}
								>
									Regresar
								</Button>
							</div>
							<div className='min-w-0 flex-1'>
								<Button
									type="button"
									variant="gradient"
									fullWidth
									loading={loadingForm}
									loadingLabel="Registrando..."
									disabled={loadingForm || !canSubmitStep2}
									onClick={handleRegister}
								>
									siguiente
								</Button>
							</div>
						</div>
					</div>
				);
			case 3:
				return (
					<div className='flex w-full flex-col gap-6 py-4 md:py-6'>
						<div className='relative z-10 w-full space-y-6'>
							<div>
								<label className='block text-sm font-semibold text-gray-700 mb-3'>
									Comprobante de pago<span className='text-red-500 ml-1'>*</span>
								</label>
								<ImageUploadDropzone file={paymentForm.file} onFileChange={handleFileChange} onFileRemove={handleFileRemove} disabled={loadingPayment} />
							</div>
							<ComboBox
								inputId='pre-reg-coordinador'
								label='Coordinador'
								required
								filterData={filteredCoordinadors}
								query={findCoordinador}
								setQuery={setFindCoordinador}
								selected={paymentForm}
								setSelected={setPaymentForm}
								placeholder='Selecciona a tu coordinador'
								property='coordinador'
							/>
						</div>
						<div className='relative z-0 flex w-full justify-between gap-4 pt-2'>
							<div className='min-w-0 flex-1'>
								<Button
									type='button'
									variant='outlineCta'
									disabled={loadingPayment}
									onClick={goBackStart}
								>
									Cancelar
								</Button>
							</div>
							<div className='min-w-0 flex-1'>
								<Button
									type='button'
									variant='gradient'
									fullWidth
									loading={loadingPayment}
									loadingLabel='Subiendo...'
									disabled={loadingPayment || !canSubmitStep3 || !userPreRegister?._id}
									onClick={handleSubmitPayment}
								>
									siguiente
								</Button>
							</div>
						</div>
					</div>
				);
			case 4:
				return (
					<div className='flex w-full flex-col gap-8 py-4 md:py-6'>
						<div className='flex justify-center items-center'>
							<div className='w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center'>
								<svg className='w-10 h-10 text-indigo-600 animate-pulse' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
								</svg>
							</div>
						</div>
						<div className='flex justify-center pt-2'>
							<Button
								type='button'
								variant='gradient'
								className='!px-8'
								onClick={goBackStart}
								icon={<ArrowRightIcon className='h-5 w-5' />}
								iconPosition='end'
							>
								Continuar
							</Button>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className='w-full h-full min-h-0 flex-1 flex flex-col bg-white px-4 md:px-6'>
			<header className='flex shrink-0 flex-col items-center px-4 pt-4 md:pt-6'>
				<div className='animate-slideDown'>
					<BrandingLogo />
				</div>
				{wizardHeader.title ? (
					<div className='mt-4 w-full max-w-4xl md:mt-5'>
						<div className='flex justify-center'>
							<h1 className='text-center text-2xl font-bold text-gray-800'>{wizardHeader.title}</h1>
						</div>
						{wizardHeader.subtitle ? (
							<div className='mt-3 flex justify-center'>
								<p className='max-w-xl text-center text-sm font-normal text-gray-500'>{wizardHeader.subtitle}</p>
							</div>
						) : null}
						<div className='mx-auto mt-4 w-full md:mt-5'>
							<StepIndicator currentStep={wizardStep} steps={WIZARD_STEPS} />
						</div>
					</div>
				) : null}
			</header>
			<main className='min-h-0 flex-1 overflow-y-auto'>
				<div className='mx-auto flex min-h-full w-full max-w-4xl flex-col justify-start px-4 md:px-8'>
					{renderStep()}
				</div>
			</main>
			<footer className='shrink-0 border-t border-gray-200/80 bg-white'>
				<CopyrightFooter className='py-4 md:py-5' />
			</footer>
			<style jsx>{`
				@keyframes slideDown {
					from { opacity: 0; transform: translateY(-12px); }
					to { opacity: 1; transform: translateY(0); }
				}
				@keyframes slideUp {
					from { opacity: 0; transform: translateY(12px); }
					to { opacity: 1; transform: translateY(0); }
				}
				.animate-slideDown { animation: slideDown 0.5s ease-out forwards; }
				.animate-slideUp { animation: slideUp 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
			`}</style>
		</div>
	);
};
